import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip as ChartTooltip,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { __fetching_data__ } from "../../components/api.js";
import { createChart } from "lightweight-charts";

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, ChartTooltip, zoomPlugin);

// ─── Config ───────────────────────────────────────────────────────────────────
const base_api_url =
  process.env.NODE_ENV === "production"
    ? window.location.protocol === "https:"
      ? process.env.REACT_APP_API_HTTPS_URL
      : process.env.REACT_APP_API_HTTP_URL
    : "/application";

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 1000;       // แถวที่แสดงในหน้าต่อ 1 ครั้ง
const PREFETCH_THRESHOLD = 500; // เมื่อ scroll ถึงแถวที่ n จาก top ของ window ปัจจุบัน ให้เริ่มโหลดหน้าถัดไป

// ─── Style ───────────────────────────────────────────────────────────────────
const scrollbarHideStyle = { msOverflowStyle: "none", scrollbarWidth: "none" };

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  n === undefined || n === null ? "0" : Number(n).toLocaleString();

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const objectToQueryString = (obj) =>
  Object.keys(obj)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(obj[k]))
    .join("&");

// ─── Skeleton Components ───────────────────────────────────────────────────────

const shimmerCSS = `
@keyframes tmShimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
`;

function Shimmer({ delay = 0 }) {
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      background: "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.07) 40%, rgba(125,211,252,0.14) 50%, rgba(56,189,248,0.07) 60%, transparent 100%)",
      animation: `tmShimmer 1.8s ease-in-out ${delay}s infinite`,
    }} />
  );
}

function SkeletonRow({ delay = 0, widths = [40, 36, 52, 20, 56] }) {
  return (
    <tr style={{ borderBottom: "1px solid #1e293b" }}>
      {widths.map((w, i) => (
        <td key={i} style={{ padding: "6px 8px", textAlign: "center" }}>
          <div style={{
            display: "inline-block", height: 9, width: w,
            borderRadius: 4, backgroundColor: "#1e293b",
            position: "relative", overflow: "hidden",
          }}>
            <Shimmer delay={delay} />
          </div>
        </td>
      ))}
    </tr>
  );
}

function SkeletonCard({ borderColor = "#1e293b", barColor = "#1e293b", delay = 0 }) {
  return (
    <div style={{
      backgroundColor: "#1e1e1e", border: `1px solid ${borderColor}`,
      borderRadius: 6, padding: 8, position: "relative", overflow: "hidden",
    }}>
      <div style={{ height: 9, width: 52, borderRadius: 4, backgroundColor: "#1e293b", marginBottom: 8, position: "relative", overflow: "hidden" }}>
        <Shimmer delay={delay} />
      </div>
      <div style={{ height: 18, width: 80, borderRadius: 4, backgroundColor: "#1e293b", marginLeft: "auto", position: "relative", overflow: "hidden" }}>
        <Shimmer delay={delay + 0.05} />
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, backgroundColor: barColor }} />
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div style={{
      position: "absolute", inset: 0,
      backgroundColor: "rgba(11,18,33,0.92)",
      backdropFilter: "blur(2px)",
      borderRadius: 8, zIndex: 50,
      display: "flex", flexDirection: "column",
      padding: 12, gap: 10, overflow: "hidden",
    }}>
      <style>{shimmerCSS}</style>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <SkeletonCard borderColor="#14532d" barColor="#22c55e" delay={0}    />
        <SkeletonCard borderColor="#7f1d1d" barColor="#ef4444" delay={0.1}  />
        <SkeletonCard borderColor="#1e293b" barColor="#64748b" delay={0.2}  />
      </div>
      <div style={{ height: 4, borderRadius: 9999, backgroundColor: "#1e293b", overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, width: "60%", backgroundColor: "#1e3a2a", borderRadius: 9999 }} />
        <Shimmer delay={0.1} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {[48, 56, 60, 48].map((w, i) => (
          <div key={i} style={{ height: 22, width: w, borderRadius: 4, backgroundColor: "#1e293b", position: "relative", overflow: "hidden" }}>
            <Shimmer delay={i * 0.08} />
          </div>
        ))}
      </div>
      <div style={{ flex: 1, borderRadius: 6, border: "1px solid #1e293b", backgroundColor: "#0B1221", overflow: "hidden", minHeight: 0 }}>
        <div style={{ backgroundColor: "#283593", padding: "6px 8px", display: "flex", justifyContent: "space-around" }}>
          {["Time", "Last", "Vol", "Type", "Sum"].map(h => (
            <div key={h} style={{ height: 10, width: 28, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.15)" }} />
          ))}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {[...Array(8)].map((_, i) => (
              <SkeletonRow key={i} delay={i * 0.06}
                widths={[38 + (i % 3) * 6, 32, 48 + (i % 2) * 10, 20, 52 + (i % 4) * 8]}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: 10, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ height: 11, width: 64, borderRadius: 3, backgroundColor: "#1e293b", position: "relative", overflow: "hidden" }}><Shimmer /></div>
          <div style={{ height: 16, width: 22, borderRadius: 9999, backgroundColor: "#1e3a6d" }} />
        </div>
        <div style={{ height: 48, borderRadius: 6, backgroundColor: "#0B1221", border: "1px solid #1e293b", position: "relative", overflow: "hidden" }}>
          {[...Array(12)].map((_, i) => (
            <div key={i} style={{
              position: "absolute", top: "30%", bottom: "30%", width: 4, borderRadius: 2,
              left: `${6 + i * 8}%`,
              backgroundColor: i % 3 === 0 ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)",
            }} />
          ))}
          <Shimmer delay={0.3} />
        </div>
      </div>
      <div style={{ borderRadius: 8, backgroundColor: "#0B1221", border: "1px solid #1e293b", padding: 8, flexShrink: 0, position: "relative", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80, padding: "4px 4px 0" }}>
          {[55, 40, 70, 30, 60, 45, 80, 35, 65, 50, 75, 42].map((h, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, height: "100%", justifyContent: "flex-end" }}>
              <div style={{ height: `${h * 0.45}%`, backgroundColor: "rgba(239,68,68,0.25)", borderRadius: "2px 2px 0 0" }} />
              <div style={{ height: `${h * 0.55}%`, backgroundColor: "rgba(34,197,94,0.25)", borderRadius: "2px 2px 0 0" }} />
            </div>
          ))}
        </div>
        <Shimmer delay={0.2} />
      </div>
    </div>
  );
}

// ─── FlipTimelineChart ─────────────────────────────────────────────────────────
const FlipTimelineChart = ({ data, flipPoints, handleRowClick }) => {
  const chartContainerRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !chartContainerRef.current) return;

    const container = chartContainerRef.current;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 48,
      timeScale: { timeVisible: true, secondsVisible: true },
      rightPriceScale: { visible: false },
      leftPriceScale:  { visible: false },
    });

    chart.applyOptions({
      layout: { background: { color: "#0B1221" }, textColor: "#94a3b8" },
      crosshair: {
        mode: 0,
        vertLines: { visible: false, color: "transparent" },
        horzLines: { visible: false, color: "transparent" },
      },
      grid: { vertLines: { color: "transparent" }, horzLines: { color: "transparent" } },
      handleScroll: false,
      handleScale:  false,
    });

    const series = chart.addLineSeries({ lineWidth: 10, priceLineVisible: false });

    const sorted = [...data].sort((a, b) => {
      const parse = (v) => {
        const str = v.Time || v.DateTime || "";
        const [datePart, timePart] = str.split(" ");
        if (!datePart || !timePart) return 0;
        const [y, mo, d] = datePart.split(".");
        const [hStr, mStr] = timePart.split(":");
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        return new Date(y, mo - 1, d, h + 7, m).getTime();
      };
      return parse(a) - parse(b);
    });

    const uniqueData = new Map();
    sorted.forEach((value) => {
      const str = value.Time || "";
      const [datePart, timePart] = str.split(" ");
      if (!datePart || !timePart) return;
      const [y, mo, d] = datePart.split(".");
      const [hStr, rest] = timePart.split(":");
      const mStr = rest ? rest.split(".")[0] : "0";
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      const dt = new Date(y, mo - 1, d, h + 7, m);
      if (isNaN(dt.getTime())) return;
      const t = Math.floor(dt.getTime() / 1000);
      const accVol = parseFloat(String(value.acc_cum_vol).replace(/,/g, ""));
      uniqueData.set(t, { time: t, accVol });
    });

    const formattedData = Array.from(uniqueData.values())
      .sort((a, b) => a.time - b.time)
      .map((p) => ({ time: p.time, value: 50, color: p.accVol > 0 ? "#22c55e" : "#ef4444" }));

    series.setData(formattedData);
    chart.timeScale().fitContent();

    chart.subscribeClick((param) => {
      if (!param?.time || !flipPoints?.length) return;
      const clickSec = param.time % 86400;
      let best = null, minDiff = Infinity;
      flipPoints.forEach((flip) => {
        const parts = flip.time.split(":");
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const s = parts[2] ? Math.floor(parseFloat(parts[2])) : 0;
        const flipSec = h * 3600 + m * 60 + s;
        const diff = Math.abs(clickSec - flipSec);
        if (diff < minDiff) { minDiff = diff; best = flip; }
      });
      if (best) handleRowClick(best.index);
    });

    const resizeObserver = new ResizeObserver(() => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    });
    resizeObserver.observe(container);

    return () => { resizeObserver.disconnect(); chart.remove(); };
  }, [data, flipPoints]);

  return <div ref={chartContainerRef} style={{ width: "100%", height: 48 }} />;
};

// ─── StackedBarChart ──────────────────────────────────────────────────────────
const StackedBarChart = ({ chartData }) => {
  const [open, setOpen] = useState(false);
  const memo = useMemo(() => chartData, [chartData]);
  if (!memo) return null;

  const opts = (h) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#94a3b8", font: { size: 11 } } },
      zoom: { pan: { enabled: true, mode: "x" }, zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: "x" } },
    },
    scales: {
      x: { stacked: true, ticks: { color: "#94a3b8", font: { size: 10 } }, grid: { color: "#1e293b" } },
      y: { stacked: true, ticks: { color: "#94a3b8", font: { size: 10 } }, grid: { color: "#1e293b" } },
    },
  });

  return (
    <div className="mt-2 rounded-lg bg-[#0B1221] border border-slate-800 p-2 relative">
      <button onClick={() => setOpen(true)} className="absolute top-2 right-2 text-slate-400 hover:text-white z-10">
        <ZoomInIcon sx={{ fontSize: 18 }} />
      </button>
      <div style={{ height: 160 }}><Bar data={memo} options={opts(160)} /></div>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0B1221] border border-slate-700 rounded-xl p-4 w-full max-w-4xl relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-slate-400 hover:text-white">
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
            <div style={{ height: "60vh" }}><Bar data={memo} options={opts(600)} /></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── useWindowedRows ──────────────────────────────────────────────────────────
/**
 * Append-only infinite scroll:
 * - เริ่มแสดง PAGE_SIZE แถวแรก
 * - เมื่อ scroll ถึงแถวที่ PREFETCH_THRESHOLD จากท้าย visibleCount ปัจจุบัน
 *   → เพิ่ม PAGE_SIZE แถวถัดไปต่อท้าย (ไม่ตัดข้อมูลเก่า ไม่วนกลับ)
 * - หยุดเมื่อแสดงครบทุกแถวแล้ว
 */
function useWindowedRows({ allRows, pageSize = PAGE_SIZE, threshold = PREFETCH_THRESHOLD }) {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  // reset visibleCount เฉพาะเมื่อ search ใหม่ (rows ถูก clear เป็น [] แล้วมีข้อมูลใหม่เข้ามา)
  const prevLengthRef = useRef(0);
  useEffect(() => {
    const prev = prevLengthRef.current;
    prevLengthRef.current = allRows.length;
    if (prev === 0 && allRows.length > 0) {
      setVisibleCount(pageSize);
    }
  }, [allRows.length, pageSize]);

  const windowStart = 0;
  const windowEnd   = Math.min(visibleCount, allRows.length);
  const visibleRows = useMemo(() => allRows.slice(0, windowEnd), [allRows, windowEnd]);

  // เก็บ state ล่าสุดไว้ใน ref เพื่อให้ callback ไม่ต้อง re-create ทุกครั้ง
  const stateRef = useRef({ visibleCount, totalRows: allRows.length });
  stateRef.current = { visibleCount, totalRows: allRows.length };

  const handleWindowScroll = useCallback((e) => {
    const el = e.currentTarget;
    const ROW_HEIGHT = 28;
    const bottomVisibleIndex = Math.floor((el.scrollTop + el.clientHeight) / ROW_HEIGHT);
    const { visibleCount: cur, totalRows: total } = stateRef.current;
    if (bottomVisibleIndex >= cur - threshold && cur < total) {
      setVisibleCount(prev => Math.min(prev + pageSize, total));
    }
  }, [pageSize, threshold]);

  return { visibleRows, windowStart, windowEnd, totalRows: allRows.length, handleWindowScroll };
}

// ─── AnalysisPanel ────────────────────────────────────────────────────────────
const AnalysisPanel = ({ isSynced, onSyncToggle, syncScrollRef }) => {
  const csrfToken = useSelector((state) => state.csrfToken);
  const todayStr = new Date().toISOString().split("T")[0];

  const [symbol, setSymbol] = useState("");
  const [date, setDate] = useState(todayStr);
  const [showDropdown, setShowDropdown] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tickmatch_symbol_history") || "[]"); } catch { return []; }
  });
  const [filtered, setFiltered] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFlipOpen, setIsFlipOpen] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");
  const [highlightedIndex, setHighlightedIndex] = useState(null);

  const [rows, setRows] = useState([]);
  const [sumBuy, setSumBuy] = useState(0);
  const [sumSell, setSumSell] = useState(0);
  const [netVol, setNetVol] = useState(0);
  const [flips, setFlips] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [rawTicks, setRawTicks] = useState([]);

  const tableScrollRef = useRef(null);
  const rowRefs = useRef([]);

  // ── Sync scroll registration ────────────────────────────────────────────────
  useEffect(() => {
    if (tableScrollRef.current && syncScrollRef) {
      if (!syncScrollRef.current.includes(tableScrollRef.current)) {
        syncScrollRef.current.push(tableScrollRef.current);
      }
    }
    return () => {
      if (syncScrollRef) {
        syncScrollRef.current = syncScrollRef.current.filter(r => r !== tableScrollRef.current);
      }
    };
  }, [syncScrollRef]);

  useEffect(() => {
    setFiltered(symbol.trim() ? history.filter(h => h.toLowerCase().includes(symbol.toLowerCase())) : history);
  }, [symbol, history]);

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filteredTicks = useMemo(() => rows.filter(r => {
    if (activeFilter === "Buy Only")  return r.type === "BUY";
    if (activeFilter === "Sell Only") return r.type === "SELL";
    if (activeFilter === "> 100K")    return parseFloat(r.vol.replace(/,/g, "")) > 100000;
    return true;
  }), [rows, activeFilter]);

  // ── Windowed rows ───────────────────────────────────────────────────────────
  const { visibleRows, windowStart, windowEnd, totalRows, handleWindowScroll } =
    useWindowedRows({ allRows: filteredTicks });

  // ── Search ──────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!symbol.trim()) return;

    setRows([]);
    setFlips([]);
    setChartData(null);
    setHasSearched(false);
    setIsFetching(true);

    const sym = symbol.toUpperCase();
    const updated = [sym, ...history.filter(s => s !== sym)].slice(0, 10);
    setHistory(updated);
    localStorage.setItem("tickmatch_symbol_history", JSON.stringify(updated));

    const values = {
      symbol: sym,
      start_date: formatDate(date),
      end_date: formatDate(date),
    };

    try {
      const response = await __fetching_data__({
        url: `${base_api_url}/tickmatch_dejavu_report1?${objectToQueryString(values)}`,
        method: "GET",
        headers: { "xsrf-token": csrfToken },
      });

      if (response.status) {
        const tickData = response.data.tickmatch || [];

        const newRows = tickData.map((value) => {
          const accNum = parseFloat(String(value.acc_cum_vol).replace(/,/g, ""));
          const volNum = parseFloat(String(value.Vol).replace(/,/g, ""));
          return {
            time: value.Time,
            last: parseFloat(value.Last).toFixed(2),
            vol: volNum.toLocaleString(),
            type: value.Type,
            acc_cum_vol: accNum.toLocaleString(),
            _accNum: accNum,
          };
        });
        setRows(newRows);

        const buyVal  = response.data.tickmatch_sum?.[0]?.sum_buy  || 0;
        const sellVal = response.data.tickmatch_sum?.[0]?.sum_sell || 0;
        setSumBuy(buyVal);
        setSumSell(sellVal);
        setNetVol(buyVal - sellVal);

        const detectedFlips = [];
        let prevAcc = null;
        tickData.forEach((value, idx) => {
          const cur = parseFloat(String(value.acc_cum_vol).replace(/,/g, ""));
          if (prevAcc !== null && ((prevAcc > 0 && cur < 0) || (prevAcc < 0 && cur > 0))) {
            const timePart = value.Time.includes(" ") ? value.Time.split(" ")[1] : value.Time;
            detectedFlips.push({ index: idx, time: timePart, fromAccVol: prevAcc.toLocaleString(), toAccVol: cur.toLocaleString() });
          }
          prevAcc = cur;
        });
        setFlips(detectedFlips);

        const labels = [], buyVols = [], sellVols = [];
        tickData.forEach((value) => {
          const price = parseFloat(value.Last).toFixed(2);
          const vol   = parseFloat(value.Vol);
          if (!labels.includes(price)) { labels.push(price); buyVols.push(0); sellVols.push(0); }
          const i = labels.indexOf(price);
          if (value.Type === "BUY") buyVols[i] += vol;
          else sellVols[i] += vol;
        });
        setChartData({
          labels,
          datasets: [
            { label: "Buy Volume",  data: buyVols,  backgroundColor: "rgba(34,197,94,0.7)", borderColor: "rgba(34,197,94,1)",  borderWidth: 1 },
            { label: "Sell Volume", data: sellVols, backgroundColor: "rgba(239,68,68,0.7)", borderColor: "rgba(239,68,68,1)",  borderWidth: 1 },
          ],
        });

        setRawTicks(tickData);
        setHasSearched(true);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => { setIsFetching(false); });
        });
      }
    } catch (err) {
      console.error("TickMatch fetch error:", err);
      setIsFetching(false);
    }
  };

  // ── Sync scroll handler ─────────────────────────────────────────────────────
  const handleScroll = useCallback((e) => {
    // Windowed scroll ก่อน
    handleWindowScroll(e);

    // ถ้า sync เปิด → broadcast scroll position ไปยัง panel อื่น
    if (!isSynced || !syncScrollRef) return;
    syncScrollRef.current.forEach(ref => {
      if (ref && ref !== tableScrollRef.current) ref.scrollTop = e.target.scrollTop;
    });
  }, [isSynced, syncScrollRef, handleWindowScroll]);

  // ── Scroll to row (flip click) ──────────────────────────────────────────────
  const handleRowClick = useCallback((absoluteIdx) => {
    setHighlightedIndex(absoluteIdx);
    const ROW_HEIGHT = 28;
    if (tableScrollRef.current) {
      if (rowRefs.current[absoluteIdx]) {
        rowRefs.current[absoluteIdx].scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        tableScrollRef.current.scrollTop = absoluteIdx * ROW_HEIGHT - 100;
      }
    }
    setTimeout(() => setHighlightedIndex(null), 2000);
  }, []);

  // ── Summary ─────────────────────────────────────────────────────────────────
  const totalBuy  = sumBuy;
  const totalSell = sumSell;
  const total  = totalBuy + totalSell;
  const buyPct = total === 0 ? 50 : (totalBuy / total) * 100;

  const netColor  = netVol === 0 ? "text-white"      : netVol < 0 ? "text-red-500"       : "text-green-500";
  const netBorder = netVol === 0 ? "border-slate-700/50" : netVol < 0 ? "border-red-900/50" : "border-green-900/50";
  const netBar    = netVol === 0 ? "bg-slate-500"    : netVol < 0 ? "bg-red-500"          : "bg-green-500";

  return (
    <div
      className="flex flex-col h-full bg-[#111827] border border-slate-700 rounded-lg p-3 shadow-lg overflow-hidden relative"
      style={scrollbarHideStyle}
    >
      {/* ── Skeleton overlay ────────────────────────────── */}
      {isFetching && <PanelSkeleton />}

      {/* ── Header Row ─────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-2 mb-2 items-end shrink-0">
        <div className="col-span-2">
          <button
            onClick={onSyncToggle}
            className={`w-full h-[40px] flex items-center justify-center gap-1 text-xs font-semibold rounded-lg transition-all
              ${isSynced ? "bg-[#0E3A6D] hover:bg-[#124a8a] text-white" : "bg-[#8FA3B5] hover:bg-[#7f95a8] text-white"}`}
          >
            {isSynced ? <><LinkOutlinedIcon sx={{ fontSize: 15 }} />SYNC</> : <><LinkOffOutlinedIcon sx={{ fontSize: 15 }} />UNSYNC</>}
          </button>
        </div>

        <div className="col-span-4 relative">
          <input
            value={symbol} placeholder=" "
            onChange={e => { setSymbol(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            className="peer w-full bg-[#111827] border border-slate-600 rounded-md px-3 py-2 text-white text-xs uppercase outline-none focus:border-cyan-400"
          />
          <label className="absolute left-3 px-1 text-[10px] bg-[#111827] text-slate-400 pointer-events-none -top-2">Symbol*</label>
          {showDropdown && filtered.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 bg-[#0f172a] border border-slate-700 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
              {filtered.map((item, i) => (
                <div key={i} onClick={() => { setSymbol(item); setShowDropdown(false); }}
                  className="px-3 py-2 text-xs text-white hover:bg-indigo-600 cursor-pointer">
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-3 relative">
          <input type="date" value={date} max={todayStr} onChange={e => setDate(e.target.value)}
            className="peer w-full bg-[#0B1221] border border-slate-600 rounded-md px-3 py-2 text-white text-xs outline-none [&::-webkit-calendar-picker-indicator]:invert" />
          <label className="absolute left-3 px-1 text-[10px] bg-[#111827] text-slate-400 pointer-events-none -top-2">Date</label>
        </div>

        <div className="col-span-3">
          <button onClick={handleSearch} disabled={isFetching}
            className="w-full h-[38px] bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded transition active:scale-95 disabled:opacity-50">
            SEARCH
          </button>
        </div>
      </div>

      {/* ── Summary Cards ──────────────────────────────── */}
      <div className="grid grid-cols-3 gap-2 mb-2 shrink-0">
        <div className="bg-[#1e1e1e] border border-green-900/50 rounded p-2 flex flex-col relative overflow-hidden">
          <span className="text-[10px] text-slate-400">Sum Buy</span>
          <span className={`font-bold text-base text-right ${hasSearched ? "text-green-500" : "text-white"}`}>{fmt(totalBuy)}</span>
          <div className="absolute bottom-0 left-0 h-[2px] bg-green-500 w-full" />
        </div>
        <div className="bg-[#1e1e1e] border border-red-900/50 rounded p-2 flex flex-col relative overflow-hidden">
          <span className="text-[10px] text-slate-400">Sum Sell</span>
          <span className={`font-bold text-base text-right ${hasSearched ? "text-red-500" : "text-white"}`}>{fmt(totalSell)}</span>
          <div className="absolute bottom-0 left-0 h-[2px] bg-red-500 w-full" />
        </div>
        <div className={`bg-[#1e1e1e] ${netBorder} border rounded p-2 flex flex-col relative overflow-hidden`}>
          <span className="text-[10px] text-slate-400">Net Acc. Vol</span>
          <span className={`font-bold text-base text-right ${netColor}`}>{fmt(netVol)}</span>
          <div className={`absolute bottom-0 left-0 h-[2px] w-full ${netBar}`} />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1 bg-red-600 rounded-full mb-2 flex overflow-hidden shrink-0">
        <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${buyPct}%` }} />
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-2 shrink-0 flex-wrap">
        {["All", "Buy Only", "Sell Only", "> 100K"].map(f => (
          <button key={f} onClick={() => setActiveFilter(f)}
            className={`text-[10px] px-3 py-1 rounded transition ${activeFilter === f ? "bg-slate-700 text-white" : "bg-[#1f2937] text-slate-400 border border-slate-600 hover:text-white"}`}>
            {f}
          </button>
        ))}
        {/* Row counter badge */}
        {totalRows > 0 && (
          <span className="ml-auto text-[10px] text-slate-500 self-center">
            {windowEnd < totalRows ? `1–${windowEnd} / ${totalRows}` : `${totalRows} rows`}
          </span>
        )}
      </div>

      {/* ── Tick Table ─────────────────────────────────── */}
      <div
        ref={tableScrollRef}
        onScroll={handleScroll}
        className="rounded overflow-auto border border-slate-800/50 bg-[#0B1221] flex-1 mb-2 min-h-0"
        style={{ maxHeight: 220, ...scrollbarHideStyle }}
      >
        <table className="w-full text-right border-collapse">
          <thead className="bg-[#283593] text-white text-[10px] font-bold sticky top-0 z-10">
            <tr>
              {["Time", "Last", "Vol", "Type", "Sum"].map(h => (
                <th key={h} className="p-2 text-center">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-xs font-mono text-slate-300">
            {visibleRows.length === 0 ? (
              <tr><td colSpan={5} className="p-4 text-center text-slate-500 text-xs">{hasSearched ? "No data" : "Enter a symbol and press Search"}</td></tr>
            ) : (
              visibleRows.map((row, relIdx) => {
                const absoluteIdx = relIdx; // windowStart always 0
                const isBuy   = row.type === "BUY";
                const accNum  = row._accNum;
                const timePart = row.time.includes(" ") ? row.time.split(" ")[1] : row.time;
                return (
                  <tr
                    key={absoluteIdx}
                    ref={el => rowRefs.current[absoluteIdx] = el}
                    className="border-b border-slate-800/30 hover:bg-slate-800/50 transition-colors"
                    style={{
                      backgroundColor:
                        highlightedIndex === absoluteIdx
                          ? "#ffeb3b30"
                          : (absoluteIdx % 2 === 0 ? "#0B1221" : "#111827"),
                    }}
                  >
                    <td className="p-2 text-center text-slate-400 text-[10px]">{timePart}</td>
                    <td className="p-2 text-yellow-500 text-center">{row.last}</td>
                    <td className="p-2 text-right text-slate-200">{row.vol}</td>
                    <td className="p-2 text-center">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold text-black ${isBuy ? "bg-green-500" : "bg-red-500"}`}>
                        {isBuy ? "B" : "S"}
                      </span>
                    </td>
                    <td className={`p-2 text-right font-mono ${accNum > 0 ? "text-green-400" : "text-red-400"}`}>
                      {row.acc_cum_vol}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Flip Section ──────────────────────────────── */}
      {hasSearched && (
        <div className="bg-[#0f172a] border border-slate-700/50 rounded-lg p-2 mb-2 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-slate-300">Flip Signal</span>
              <span className="bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full">{flips.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-2 bg-red-500 rounded-sm" />
                <span className="text-[9px] text-slate-400">Vol&lt;0</span>
                <div className="w-3 h-2 bg-green-500 rounded-sm ml-1" />
                <span className="text-[9px] text-slate-400">Vol&gt;0</span>
              </div>
              <button onClick={() => setIsFlipOpen(v => !v)} className="text-slate-400 hover:text-white">
                {isFlipOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </button>
            </div>
          </div>

          {isFlipOpen && rawTicks.length > 0 && (
            <div className="rounded-lg bg-[#0B1221] border border-slate-800 mb-2 overflow-hidden">
              <FlipTimelineChart data={rawTicks} flipPoints={flips} handleRowClick={handleRowClick} />
            </div>
          )}
        </div>
      )}

      {/* ── Stacked Bar Chart ──────────────────────────── */}
      {hasSearched && chartData && <StackedBarChart chartData={chartData} />}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TickMatch() {
  const [isSynced, setIsSynced] = useState(false);
  const syncScrollRef = useRef([]);

  return (
    <div className="w-full h-screen bg-[#0B1221] text-white p-4 flex flex-col gap-4 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-0">
        <AnalysisPanel isSynced={isSynced} onSyncToggle={() => setIsSynced(v => !v)} syncScrollRef={syncScrollRef} />
        <AnalysisPanel isSynced={isSynced} onSyncToggle={() => setIsSynced(v => !v)} syncScrollRef={syncScrollRef} />
      </div>
    </div>
  );
}